import sharedDatasInstance from "./sharedDatas";

// Stores the last identify params so we can re-identify after azienda switch
let _lastIdentifyParams = null;
let _aziendaChangeSubscribed = false;

/**
 * SHA-256 hash using Web Crypto API.
 * Input is lowercased and trimmed before hashing, as per Gainsight PX guidelines.
 */
async function hashSHA256(value) {
  if (!value) return null;
  const normalized = value.toString().toLowerCase().trim();
  const encoder = new TextEncoder();
  const data = encoder.encode(normalized);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Checks whether the user has accepted profiling cookies.
 *
 * Consent source priority:
 * 1. sharedDatas "cookieConsent" (set by future consent management integration)
 * 2. Default: false (conservative — no profiling data sent until consent is managed)
 *
 * NOTE: The app currently has no cookie consent management system in production.
 * When one is integrated (e.g. OneTrust), update this function or populate
 * sharedDatas "cookieConsent" accordingly.
 */
function hasProfilingConsent() {
  const consent = sharedDatasInstance.getPropertyByName("cookieConsent");
  if (!consent) return false;
  return consent.profilingCookies === true;
}

/**
 * Initializes the Gainsight PX tag via the manual loader defined in index.html.
 */
function initTag() {
  if (typeof window.initializeGainsightPX === "function") {
    window.initializeGainsightPX({ force: true });
  }
}

/**
 * Sends the Gainsight PX identify call with hashed IDs and
 * cookie-consent-aware attribute filtering.
 *
 * @param {object} params
 * @param {string} params.tsId - TSID user code (JWT "sub") to hash as user.id
 * @param {string} params.itemId - Tenant itemId to hash as account.id
 * @param {object} params.userModel - User model from sharedDatas
 * @param {object} params.tenantModel - Tenant model from sharedDatas
 */
async function identify({ tsId, itemId, userModel, tenantModel }) {
  if (typeof window.aptrinsic !== "function") return;

  const userId = await hashSHA256(tsId);
  if (!userId) return;

  // User object — always include id and non-profiling attributes
  const userObj = { id: userId };

  if (userModel) {
    if (userModel.locale) {
      userObj.language = userModel.locale;
    }
    if (userModel.createdOn) {
      userObj.signUpDate = new Date(userModel.createdOn).getTime();
    }
  }

  // Profiling attributes — only if the user accepted profiling cookies
  if (hasProfilingConsent() && userModel) {
    if (userModel.email) userObj.email = userModel.email;
    if (userModel.name) userObj.firstName = userModel.name;
    if (userModel.surname) userObj.lastName = userModel.surname;
  }

  // Cookie consent flags — sent to Gainsight if consent data is available
  const consent = sharedDatasInstance.getPropertyByName("cookieConsent");
  if (consent) {
    userObj.necessaryCookies = consent.necessaryCookies ?? false;
    userObj.performanceCookies = consent.performanceCookies ?? false;
    userObj.functionalCookies = consent.functionalCookies ?? false;
    userObj.advertisingCookies = consent.advertisingCookies ?? false;
    userObj.profilingCookies = consent.profilingCookies ?? false;
    userObj.analyticsProfilingCookies = consent.analyticsProfilingCookies ?? false;
  }

  // Account object
  const accountObj = {};

  if (itemId) {
    // Standard case: itemId available — hash it for both id and itemId attribute
    const hashedItemId = await hashSHA256(itemId);
    accountObj.id = hashedItemId;
    accountObj.itemId = hashedItemId;
  } else {
    // Fallback: use tenantId + suffix, stored as accountCustomId
    const tenantId = sharedDatasInstance.getPropertyByName("tenantId");
    const hashedFallback = await hashSHA256(tenantId + "_aml");
    accountObj.id = hashedFallback;
    accountObj.accountCustomId = hashedFallback;
  }

  // Non-profiling account attributes
  if (tenantModel && tenantModel.countryCode) {
    accountObj.countryCode = tenantModel.countryCode;
  }

  // Profiling account attributes
  if (hasProfilingConsent() && tenantModel) {
    if (tenantModel.name) accountObj.businessName = tenantModel.name;
    if (tenantModel.vatNumber) accountObj.vatIdentifier = tenantModel.vatNumber;
  }

  window.aptrinsic("identify", userObj, accountObj);

  // Store params for re-identify on azienda change
  _lastIdentifyParams = { tsId, itemId, userModel, tenantModel };

  // Subscribe once to aziendaId changes for reset + re-identify
  if (!_aziendaChangeSubscribed) {
    _aziendaChangeSubscribed = true;
    sharedDatasInstance.subscribe((propertyName) => {
      if (propertyName === "aziendaId" && _lastIdentifyParams) {
        reset();
        identify(_lastIdentifyParams);
      }
    });
  }
}

/**
 * Sends a custom event to Gainsight PX.
 * Mandatory Global Context properties (commercialOfferName) are injected automatically.
 *
 * @param {string} eventName - Event name following convention: AML_Module_Feature
 * @param {object} [properties={}] - Optional event properties
 */
function track(eventName, properties = {}) {
  if (typeof window.aptrinsic !== "function") return;

  const globalContext = {
    commercialOfferName: "AML",
  };

  const aziendaId = sharedDatasInstance.getPropertyByName("aziendaId");
  if (aziendaId) {
    globalContext.companyContext = String(aziendaId);
  }

  window.aptrinsic("track", eventName, {
    ...globalContext,
    ...properties,
  });
}

/**
 * Resets the Gainsight PX session.
 * Call on logout or when switching azienda/account.
 */
function reset() {
  if (typeof window.aptrinsic !== "function") return;
  window.aptrinsic("reset");
}

const gainsightService = {
  hashSHA256,
  initTag,
  identify,
  track,
  reset,
};

export default gainsightService;
